import React, { useState, useContext } from 'react';
import styles from './styles.module.css';
import { Grid, IconButton, InputAdornment } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ROUTES, STORAGE_KEYS, REGISTRATION_TYPE, REGISTRATION_CHANNEL, BLOCKED_PASSWORDS, COUNTRY_CONFIGRATION, DEFAULT_TRIAL_DAYS, DEFAULT_COUNTRY, DEFAULT_CURRENCY } from 'const';
import AuthService from 'services/AuthService';
import { useHistory, Link, useLocation } from 'react-router-dom';
// import Loader from 'components/utils/Loading';
import { OnlinePurchaseContext } from 'Context/OnlinePurchaseContext';
import { isValidEmail, isValidPassword } from 'helpers/StringHelper';
import TextFieldBGGray from 'components/utils/CustomComponents/TextFieldBGGray';
import Sanoft_log from 'images/sanoft_logo_new.png';
import ButtonBlue from 'components/utils/CustomComponents/ButtonBlue';

export default function NewAccount(props) {
  const { isLoading: isLoadingInPurchaseContext, setPurchaseData, purchaseData } = useContext(OnlinePurchaseContext);
  const [data, setData] = useState({ name: '', email: '', first_name: '', last_name: '', username: '', password: '', mobile: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const registration_type = queryParams.get('registration_type');
  const registration_channel = queryParams.get('registration_channel');
  const subscription_key = queryParams.get('subscription_key');
  const { subscriptionsAndAddons, loadSubscriptionsAndAddons } = useContext(OnlinePurchaseContext);

  React.useEffect(() => {
    loadSubscriptionsAndAddons();
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (!subscriptionsAndAddons || !subscription_key) return;

    const selectedPlan = (subscriptionsAndAddons?.subscriptions || []).find(s => s.key === subscription_key);
    if (!selectedPlan) return;

    // Sync plan from URL subscription_key if purchaseData doesn't match
    setPurchaseData((prevState) => {
      if (prevState?.subscription?.key === subscription_key) return prevState; // already correct, no update
      const amount = selectedPlan.monthly_price || 0;
      const country = selectedPlan.country || DEFAULT_COUNTRY;
      const tax = amount * (COUNTRY_CONFIGRATION[country]?.tax || 0);
      return {
        ...prevState,
        subscription: { id: selectedPlan.id, key: selectedPlan.key, validity: 30 },
        tax,
        amount,
        payable: amount + tax,
        country,
        currency: selectedPlan.currency || DEFAULT_CURRENCY,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionsAndAddons, subscription_key]);

  const getDealerFromStorage = () => {
    const itemStr = localStorage.getItem('dealer');

    if (!itemStr) return null;

    try {
      const item = JSON.parse(itemStr);
      const now = new Date();

      if (now.getTime() > item.expiry) {
        localStorage.removeItem('dealer'); // expired
        return null;
      }

      return Number(item.value);
    } catch (e) {
      localStorage.removeItem('dealer'); // corrupted data
      return null;
    }
  };

  const signupBtnPressed = async (e) => {
    setErrorMessage(null);

    if (!data.name || !data.username || !data.email || !data.first_name || !data.mobile || !data.password) {
      setErrorMessage('Required field should not be empty.\n Fill the form correctly.');
      return;
    }

    if (!isValidEmail(data.email)) {
      setErrorMessage('Invalid email provided');
      return;
    }

    if (BLOCKED_PASSWORDS.includes(data.password) || !isValidPassword(data.password)) {
      setErrorMessage('Password must be at least 8 characters long and contain both letters and numbers.');
      return;
    }

    if (data.password.toLowerCase() === data.username.toLowerCase()) {
      setErrorMessage('Password cannot be the same as your username.');
      return;
    }

    if (!/^[a-zA-Z0-9._-]+$/.test(data.username)) {
      setErrorMessage('Username can only contain letters, numbers, underscores (_), dots (.), or hyphens (-).');
      return;
    }

    if (registration_type === REGISTRATION_TYPE.NEW && registration_channel === REGISTRATION_CHANNEL.ONLINE) {
      setPurchaseData((prevState) => ({ ...prevState, shop_details: data }));
      history.push(ROUTES.PROCESS_PAYMENT);
      return;
    }

    setIsLoading(true);
    try {
      const dealer_id = purchaseData.dealer_id ?? getDealerFromStorage();
      const signupData = {
        ...data,
        mac_id: localStorage.getItem(STORAGE_KEYS.MAC_ID) || null,
        registration_key: localStorage.getItem(STORAGE_KEYS.REGISTRSATION_KEY) || null,
        dealer_id: dealer_id,
        subscription_id: purchaseData?.subscription?.id,
        plan_key: registration_type === REGISTRATION_TYPE.FREE ? 'free' : (purchaseData?.subscription?.key || null),
      };
      const params = {
        registration_channel,
        registration_type,
        [REGISTRATION_TYPE.FREE]: registration_type === REGISTRATION_TYPE.FREE,
      };

      const error = await AuthService.createAccount(signupData, params);
      if (error) {
        setErrorMessage(error.message || (typeof error === 'string' ? error : 'Registration failed'));
        return;
      }

      if (
        registration_type === REGISTRATION_TYPE.TRIAL ||
        registration_type === REGISTRATION_TYPE.FREE ||
        registration_channel === REGISTRATION_CHANNEL.KEY
      ) {
        localStorage.removeItem(STORAGE_KEYS.REGISTRSATION_KEY);
        localStorage.removeItem(STORAGE_KEYS.MAC_ID);
        history.push(ROUTES.ONBOARDING);
        return;
      }

      // Default redirect if no other condition met
      history.push(ROUTES.ONBOARDING);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.stylishContainer}>
      <div className={styles.formCard}>
        <div className={styles.formHeader}>
          <IconButton onClick={() => history.goBack()} className={styles.stylishBackButton}>
            <ArrowBackIcon />
          </IconButton>
          <div className={styles.headerTitleSection}>
            {registration_type === REGISTRATION_TYPE.TRIAL ? (
              <>
                <h2 className={styles.animate1}>Start your free trial</h2>
                <p className={`${styles.priceNote} ${styles.animate2}`}>
                  {COUNTRY_CONFIGRATION[purchaseData?.country]?.trial_days || DEFAULT_TRIAL_DAYS} days of full access. No credit card required.
                </p>
              </>
            ) : (
              <>
                <h2 className={styles.animate1}>Create your account</h2>
                <p className={`${styles.priceNote} ${styles.animate2}`}>Join thousands of businesses using Sanoft POS.</p>
              </>
            )}
          </div>
          <img src={Sanoft_log} alt="Sanoft POS logo" className={styles.formLogo} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); signupBtnPressed(); }}>
          <Grid container spacing={4}>
            {/* Section 1: Business Information */}
            <Grid item xs={12} md={6} className={styles.animate2}>
              <div className={styles.sectionHeading}>Business Information</div>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextFieldBGGray
                    name="shopname"
                    label="Shop Name"
                    variant="outlined"
                    fullWidth
                    value={data.name || ''}
                    autoComplete="off"
                    onChange={(event) => { const v = event.target.value; setData(prev => ({ ...prev, name: v })); }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldBGGray
                    name="email"
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    fullWidth
                    value={data.email || ''}
                    autoComplete="off"
                    onChange={(event) => { const v = event.target.value; setData(prev => ({ ...prev, email: v })); }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldBGGray
                    name="mobilenumber"
                    label="Mobile Number"
                    variant="outlined"
                    fullWidth
                    value={data.mobile || ''}
                    autoComplete="off"
                    onChange={(event) => { const v = event.target.value; setData(prev => ({ ...prev, mobile: v })); }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Section 2: Account Security */}
            <Grid item xs={12} md={6} className={styles.animate3}>
              <div className={styles.sectionHeading}>Account Security</div>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextFieldBGGray
                    name="firstname"
                    label="Full Name"
                    variant="outlined"
                    fullWidth
                    value={data.first_name || ''}
                    autoComplete="off"
                    onChange={(event) => { const v = event.target.value; setData(prev => ({ ...prev, first_name: v, last_name: '-' })); }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldBGGray
                    name="username"
                    label="Username"
                    variant="outlined"
                    fullWidth
                    value={data.username || ''}
                    autoComplete="new-password"
                    onChange={(event) => { const v = event.target.value.trim(); setData(prev => ({ ...prev, username: v })); }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldBGGray
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    value={data.password || ''}
                    autoComplete="new-password"
                    onChange={(event) => { const v = event.target.value.trim(); setData(prev => ({ ...prev, password: v })); }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {errorMessage && (
              <Grid item xs={12} className={styles.animate4}>
                <div className={styles.stylishError}>
                  <span>{errorMessage}</span>
                </div>
              </Grid>
            )}

            <Grid item xs={12} className={styles.animate4}>
              <ButtonBlue
                onClick={signupBtnPressed}
                disabled={isLoading}
                fullWidth
                style={{
                  padding: '14px',
                  fontSize: '17px',
                  fontWeight: '800',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  boxShadow: '0 8px 16px -4px rgba(37, 99, 235, 0.4)',
                  textTransform: 'none',
                  letterSpacing: '0.01em'
                }}
              >
                {isLoading || isLoadingInPurchaseContext ? 'Creating Account...' : 'Get Started'}
              </ButtonBlue>
            </Grid>

            <Grid item xs={12} className={`${styles.stylishFooter} ${styles.animate4}`}>
              Already have an account?{' '}
              <Link to={ROUTES.LOGIN} className={styles.link}>
                Log in
              </Link>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
